import { useRef, useState, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface FileUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  accept?: string;
  maxBytes?: number;
  onSubmit: (file: File) => void;
  isPending?: boolean;
  submitLabel?: string;
  submitError?: string;
  extras?: ReactNode;
}

export const FileUploadDialog = ({
  open,
  onOpenChange,
  title,
  description,
  accept,
  maxBytes,
  onSubmit,
  isPending = false,
  submitLabel = 'Загрузить',
  submitError,
  extras,
}: FileUploadDialogProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<File | null>(null);

  const reset = (): void => {
    if (inputRef.current) inputRef.current.value = '';
    setSelected(null);
    setLocalError(undefined);
  };

  const handleOpenChange = (next: boolean): void => {
    onOpenChange(next);
    if (!next) reset();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0] ?? null;
    setLocalError(undefined);
    if (file && maxBytes !== undefined && file.size > maxBytes) {
      setLocalError(
        `Файл слишком большой (лимит ${String(Math.floor(maxBytes / 1024 / 1024))} MB)`,
      );
      setSelected(null);
      return;
    }
    setSelected(file);
  };

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (!selected) {
      setLocalError('Выберите файл');
      return;
    }
    onSubmit(selected);
  };

  const errorMessage = submitError ?? localError;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {extras}
          <div className="space-y-2">
            <Label htmlFor="file-upload">Файл</Label>
            <Input
              id="file-upload"
              type="file"
              accept={accept}
              ref={inputRef}
              onChange={handleFileChange}
            />
          </div>
          {errorMessage ? <p className="text-sm text-destructive">{errorMessage}</p> : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                handleOpenChange(false);
              }}
            >
              Отмена
            </Button>
            <Button type="submit" disabled={isPending || !selected}>
              {isPending ? 'Загрузка…' : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
